// How the Work Effort panel's Data Sources list sorts and labels a customer's tables.
//
// Tables come from GET /api/admin/schemas/{id}: the DuckDB registry row (table_name,
// row_count, column_count, created_at, updated_at, source_file) plus table_metadata's
// source_type, product_id and last_synced_at (routers/admin._attach_table_sources).
// The registry's `source_file` is NOT where a table came from -- the registry reconcile
// tags adopted API tables with it -- so nothing here classifies or names a table by it.
//
// Plain module, no imports, so tests can run it under node.

// table_metadata.source_type values written by file loads (routers/upload.py, the
// ingestion service's file/hydration/manual paths, the admin bulk load).
const FILE_SOURCES = new Set(['file', 'hydration', 'manual', 'bulk'])

// A sync outcome after which the run's rows landed (services/connection_status.LANDED_STATUSES).
const LANDED = new Set(['completed', 'completed_with_errors', 'success'])

const ACRONYMS = new Set(['UKG', 'REST', 'SOAP', 'WFM', 'API', 'HR', 'ADP', 'SAP'])

// "cust_<id>_api_cobra_details" -> "Cobra Details"
export function formatTableName(name) {
  const n = name || ''
  return (/^cust_.+?_api_/.test(n) ? n.replace(/^cust_.+?_api_/, '') : n.replace(/^cust_[^_]+_/, ''))
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
}

// "UKG_PRO_RECRUITING_REST" -> "UKG Pro Recruiting REST"
export function productLabel(productId) {
  if (!productId) return ''
  return String(productId).split('_').filter(Boolean).map(w => {
    const up = w.toUpperCase()
    if (ACRONYMS.has(up)) return up
    if (up === 'RAAS') return 'RaaS'
    return up.charAt(0) + up.slice(1).toLowerCase()
  }).join(' ')
}

// The total rows an upload record says it loaded: row_counts is {table: rows}.
export function uploadRowCount(u) {
  const rc = u?.row_counts
  if (rc && typeof rc === 'object') {
    return Object.values(rc).reduce((s, n) => s + (Number(n) || 0), 0)
  }
  return Number(u?.row_count) || 0
}

// The date a table last loaded: table_metadata.last_synced_at, else the registry's
// updated_at / created_at (file loads leave last_synced_at empty).
export const tableDate = (t) => t?.last_synced_at || t?.updated_at || t?.created_at || null

/**
 * Split tables into the panel's sections.
 *
 *   connected  tables a connection sync wrote (a non-file source_type)
 *   uploaded   tables a file load wrote (a file source_type, or named in an upload's
 *              tables_created); each carries `upload_file` when an upload names it
 *   other      tables whose source is not recorded (no table_metadata row, or the
 *              metadata read failed) -- listed, never guessed into a section
 *   uploadRows upload records whose tables are not in the list: one per file name (the
 *              newest record), rows summed from row_counts
 *
 * `stale` is set on a connected table whose last_synced_at is older than the start of
 * its connection's newest FULL sync (`last_full_run`: the newest scheduled run that landed
 * rows) -- never against a manual pull, which can name a single endpoint.
 */
export function classifyTables(tables = [], uploads = [], connections = []) {
  const fileOfTable = new Map()
  for (const u of uploads) {
    for (const t of (u.tables_created || [])) {
      const prev = fileOfTable.get(t)
      if (!prev || String(u.uploaded_at || '') > String(prev.uploaded_at || '')) fileOfTable.set(t, u)
    }
  }
  const lastLanded = new Map()
  for (const c of connections) {
    const r = c?.last_full_run
    if (c?.product_id && r && LANDED.has(r.status) && r.started_at) lastLanded.set(c.product_id, r)
  }

  const connected = [], uploaded = [], other = []
  const listed = new Set()
  for (const t of tables) {
    listed.add(t.table_name)
    const upload = fileOfTable.get(t.table_name)
    const src = t.source_type
    if (upload || FILE_SOURCES.has(src)) {
      uploaded.push({ ...t, upload_file: upload?.file_name || null })
    } else if (src) {
      const run = lastLanded.get(t.product_id)
      const synced = t.last_synced_at ? new Date(t.last_synced_at).getTime() : null
      const stale = !!(run && synced != null && synced < new Date(run.started_at).getTime())
      connected.push({ ...t, stale, last_run_at: stale ? (run.completed_at || run.started_at) : null })
    } else {
      other.push(t)
    }
  }

  const byFile = new Map()
  for (const u of uploads) {
    const tablesOf = u.tables_created || []
    if (tablesOf.length && tablesOf.some(t => listed.has(t))) continue
    const key = (u.file_name || '').toLowerCase()
    const prev = byFile.get(key)
    if (!prev || String(u.uploaded_at || '') > String(prev.uploaded_at || '')) byFile.set(key, u)
  }
  const uploadRows = [...byFile.values()].map(u => ({ ...u, rows: uploadRowCount(u) }))

  return { connected, uploaded, other, uploadRows }
}

// The newest last_synced_at among a connection's tables (by product_id): when its loads
// landed, whether or not the run history recorded the run that loaded them.
export function lastLandedByProduct(tables = []) {
  const out = {}
  for (const t of tables) {
    const at = t?.last_synced_at ? new Date(t.last_synced_at).getTime() : NaN
    if (!t?.product_id || Number.isNaN(at)) continue
    if (!out[t.product_id] || at > new Date(out[t.product_id]).getTime()) out[t.product_id] = t.last_synced_at
  }
  return out
}
